import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { MdModeEdit, MdOutlineEventBusy, MdOutlineDeleteOutline } from "react-icons/md";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader/Loader";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as resourceService from "../../../services/resourceService";
import { nameWithSkill } from "../../../services/skillService";
import useSkills from "../../../hooks/useSkills";

const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
};

const labelStyle = { fontSize: "13px", fontWeight: 500 };

const iconBtn = {
  background: "none",
  border: "none",
  padding: "4px",
  cursor: "pointer",
  color: "#C0335A",
  display: "inline-flex",
  alignItems: "center",
};

// datetime-local gives "YYYY-MM-DDTHH:MM"; show it as "YYYY-MM-DD HH:MM".
const showDateTime = (value) => String(value || "").replace("T", " ").slice(0, 16);

const SaveButton = ({ saving, onClick, label = "Save" }) => (
  <button className="btn Button primaryBackground text-white px-4" onClick={onClick} disabled={saving}>
    {saving ? <Loader style={{ width: 40, height: 20 }} /> : label}
  </button>
);

/* ------------------------------------------------------------------ *
 *  Table rows (module scope so editing state is never lost on re-render)
 * ------------------------------------------------------------------ */
function ResourceRow({ item, index, onEdit, onBlocks, onToggle }) {
  return (
    <tr
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={{ transition: "background 0.15s", opacity: item.is_active ? 1 : 0.6 }}
    >
      <td style={tdStyle}>{index + 1}</td>
      <td style={tdStyle}>
        <div style={{ fontWeight: 500 }}>{item.name}</div>
        {item.notes && <div style={{ fontSize: "12px", color: "#6B7280" }}>{item.notes}</div>}
      </td>
      <td style={tdStyle}>{item.type_name}</td>
      <td style={tdStyle}>
        {item.crew?.length ? (
          <span style={{ fontSize: "13px" }}>{item.crew.map((c) => c.name).join(", ")}</span>
        ) : (
          <span style={{ fontSize: "13px", color: "#9CA3AF" }}>-</span>
        )}
      </td>
      <td style={tdStyle}>
        <button type="button" style={iconBtn} onClick={() => onEdit(item)} title="Edit">
          <MdModeEdit size={17} />
        </button>
        <button type="button" style={iconBtn} onClick={() => onBlocks(item)} title="Out-of-service blocks">
          <MdOutlineEventBusy size={17} />
        </button>
      </td>
      <td style={tdStyle}>
        <Form.Check
          type="switch"
          checked={!!item.is_active}
          onChange={() => onToggle(item)}
          id={`resource-switch-${item.id}`}
        />
      </td>
    </tr>
  );
}

function TypeRow({ item, index, onEdit, onToggle }) {
  return (
    <tr
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={{ transition: "background 0.15s", opacity: item.is_active ? 1 : 0.6 }}
    >
      <td style={tdStyle}>{index + 1}</td>
      <td style={tdStyle}>{item.name}</td>
      <td style={tdStyle}>{item.active_resources}</td>
      <td style={tdStyle}>
        <button type="button" style={iconBtn} onClick={() => onEdit(item)} title="Rename">
          <MdModeEdit size={17} />
        </button>
      </td>
      <td style={tdStyle}>
        <Form.Check
          type="switch"
          checked={!!item.is_active}
          onChange={() => onToggle(item)}
          id={`resource-type-switch-${item.id}`}
        />
      </td>
    </tr>
  );
}

function SkeletonRows({ columns }) {
  return Array(4)
    .fill(null)
    .map((_, i) => (
      <tr key={i}>
        {columns.map((w, j) => (
          <td key={j} style={tdStyle}>
            <Skeleton width={w} />
          </td>
        ))}
      </tr>
    ));
}

/* ------------------------------------------------------------------ *
 *  Panels
 * ------------------------------------------------------------------ */
function ResourceFormPanel({ show, editing, types, crewOptions, labels, onHide, onSaved }) {
  const { skills } = useSkills();
  const [crewIds, setCrewIds] = useState([]);
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset whenever the panel opens for a different record.
  useEffect(() => {
    if (!show) return;
    setName(editing?.name || "");
    setTypeId(editing ? String(editing.type_id) : types[0] ? String(types[0].id) : "");
    setNotes(editing?.notes || "");
    setCrewIds((editing?.crew || []).map((c) => c.user_id));
    setError("");
  }, [show, editing, types]);

  const toggleCrew = (userId) =>
    setCrewIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));

  const submit = async () => {
    if (!name.trim()) return setError(`${labels.singular} name is required`);
    if (!typeId) return setError("Choose a type");
    setSaving(true);
    try {
      const body = { name: name.trim(), type_id: Number(typeId), notes: notes.trim() || null };
      const res = editing
        ? await resourceService.updateResource(editing.id, body)
        : await resourceService.createResource(body);
      if (res?.success) {
        // The pool is its own call. Only made when it changed, so editing just the
        // name never touches who may use the resource.
        const before = (editing?.crew || []).map((c) => c.user_id);
        const poolChanged = crewIds.length !== before.length || crewIds.some((id) => !before.includes(id));
        const resourceId = editing ? editing.id : res.data?.id;
        let poolFailure = null;
        if (poolChanged && resourceId) {
          const poolRes = await resourceService.setResourceCrew(resourceId, crewIds);
          if (!poolRes?.success) poolFailure = resourceService.resultMessage(poolRes, "Could not save the crew");
        }
        if (poolFailure) {
          toast.error(`${editing ? labels.singular + " updated" : labels.singular + " added"}, but the crew was not saved: ${poolFailure}`);
        } else {
          toast.success(editing ? `${labels.singular} updated` : `${labels.singular} added`);
        }
        onSaved();
      } else {
        setError(resourceService.resultMessage(res));
      }
    } catch (e) {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Types that are inactive can't take new resources, but an existing resource
  // keeps showing its current type.
  const options = types.filter((t) => t.is_active || (editing && t.id === editing.type_id));

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title={editing ? `Edit ${labels.singular}` : `New ${labels.singular}`}
      footer={
        <>
          <button className="btn btn-outline-secondary px-4" onClick={onHide}>
            Cancel
          </button>
          <SaveButton saving={saving} onClick={submit} label={editing ? "Save" : "Add"} />
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label" style={labelStyle}>
          Name
        </label>
        <input
          type="text"
          className="form-control"
          value={name}
          maxLength={150}
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
          placeholder={`e.g. ${labels.nameExample}`}
        />
      </div>
      <div className="mb-3">
        <label className="form-label" style={labelStyle}>
          Type
        </label>
        <select className="form-select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {options.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label" style={labelStyle}>
          Notes
        </label>
        <input
          type="text"
          className="form-control"
          value={notes}
          maxLength={500}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
        />
      </div>
      {crewOptions.length > 0 && (
        <div className="mb-3">
          <label className="form-label" style={labelStyle}>
            Crew who use this
          </label>
          <div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "6px" }}>
            When this {labels.singular.toLowerCase()} is chosen on a booking, these are the people offered for its
            steps. If only one person has the skill a step needs, they are picked automatically.
          </div>
          <div
            style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "6px 10px" }}
          >
            {crewOptions.map((c) => (
              <Form.Check
                key={c.user_id}
                type="checkbox"
                id={`pool-crew-${c.user_id}`}
                label={nameWithSkill(c.name, c.skill_key, skills)}
                checked={crewIds.includes(c.user_id)}
                onChange={() => toggleCrew(c.user_id)}
              />
            ))}
          </div>
        </div>
      )}
      {error && <small className="text-danger">{error}</small>}
    </SlidePanel>
  );
}

function TypeFormPanel({ show, editing, labels, onHide, onSaved }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setName(editing?.name || "");
    setError("");
  }, [show, editing]);

  const submit = async () => {
    if (!name.trim()) return setError("Type name is required");
    setSaving(true);
    try {
      const res = editing
        ? await resourceService.updateResourceType(editing.id, { name: name.trim() })
        : await resourceService.createResourceType({ name: name.trim() });
      if (res?.success) {
        toast.success(editing ? "Type updated" : "Type added");
        onSaved();
      } else {
        setError(resourceService.resultMessage(res));
      }
    } catch (e) {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title={editing ? "Rename Type" : "New Type"}
      footer={
        <>
          <button className="btn btn-outline-secondary px-4" onClick={onHide}>
            Cancel
          </button>
          <SaveButton saving={saving} onClick={submit} label={editing ? "Save" : "Add"} />
        </>
      }
    >
      <div className="mb-3" onKeyDown={(e) => e.key === "Enter" && submit()}>
        <label className="form-label" style={labelStyle}>
          Type name
        </label>
        <input
          type="text"
          className="form-control"
          value={name}
          maxLength={100}
          autoFocus
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
          placeholder={`e.g. ${labels.typeExample}`}
        />
        <small className="text-muted">Types group your {labels.plural.toLowerCase()} so they are easy to find.</small>
        {error && (
          <div>
            <small className="text-danger">{error}</small>
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

function BlocksPanel({ show, resource, onHide }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!resource) return;
    setLoading(true);
    try {
      const res = await resourceService.getResourceBlocks(resource.id);
      setBlocks(res?.success ? res.data : []);
    } catch (e) {
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [resource]);

  useEffect(() => {
    if (!show) return;
    setStartAt("");
    setEndAt("");
    setReason("");
    setError("");
    load();
  }, [show, load]);

  const add = async () => {
    if (!startAt || !endAt) return setError("Choose a start and an end");
    if (endAt <= startAt) return setError("End must be after start");
    setSaving(true);
    try {
      const res = await resourceService.createResourceBlock(resource.id, {
        start_at: startAt,
        end_at: endAt,
        reason: reason.trim() || null,
      });
      if (res?.success) {
        setStartAt("");
        setEndAt("");
        setReason("");
        setError("");
        load();
      } else {
        setError(resourceService.resultMessage(res));
      }
    } catch (e) {
      setError("Could not add the block. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (block) => {
    try {
      const res = await resourceService.deleteResourceBlock(block.id);
      if (res?.success) load();
      else toast.error(resourceService.resultMessage(res));
    } catch (e) {
      toast.error("Could not remove the block");
    }
  };

  return (
    <SlidePanel show={show} onHide={onHide} title={`Out of service: ${resource?.name || ""}`} width={520}>
      <p style={{ fontSize: "13px", color: "#6B7280" }}>
        Block this resource for maintenance or repairs. Bookings that overlap a block are flagged as a clash, but staff
        can still proceed.
      </p>

      <div className="mb-3 p-3" style={{ background: "#F9FAFB", borderRadius: "10px" }}>
        <div className="row g-2">
          <div className="col-sm-6">
            <label className="form-label" style={labelStyle}>
              From
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={startAt}
              onChange={(e) => {
                setStartAt(e.target.value);
                setError("");
              }}
            />
          </div>
          <div className="col-sm-6">
            <label className="form-label" style={labelStyle}>
              To
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={endAt}
              min={startAt || undefined}
              onChange={(e) => {
                setEndAt(e.target.value);
                setError("");
              }}
            />
          </div>
          <div className="col-12">
            <input
              type="text"
              className="form-control"
              value={reason}
              maxLength={255}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional), e.g. Under repair"
            />
          </div>
        </div>
        {error && (
          <div className="mt-2">
            <small className="text-danger">{error}</small>
          </div>
        )}
        <div className="mt-3 text-end">
          <SaveButton saving={saving} onClick={add} label="Add block" />
        </div>
      </div>

      <h6 style={{ fontSize: "13px", fontWeight: 600 }}>Upcoming blocks</h6>
      {loading ? (
        <Skeleton count={2} height={36} />
      ) : blocks.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#6B7280" }}>No upcoming blocks.</p>
      ) : (
        blocks.map((b) => (
          <div
            key={b.id}
            className="d-flex align-items-center justify-content-between py-2"
            style={{ borderBottom: "1px solid #F3F4F6", fontSize: "13px" }}
          >
            <div>
              <div>
                {showDateTime(b.start_at)} to {showDateTime(b.end_at)}
              </div>
              {b.reason && <div style={{ color: "#6B7280" }}>{b.reason}</div>}
            </div>
            <button type="button" style={iconBtn} onClick={() => remove(b)} title="Remove">
              <MdOutlineDeleteOutline size={18} />
            </button>
          </div>
        ))
      )}
    </SlidePanel>
  );
}

function DeactivatePanel({ pending, labels, busy, onCancel, onConfirm }) {
  return (
    <SlidePanel show={!!pending} onHide={onCancel} title={`Deactivate ${labels.singular}`}>
      <p style={{ fontSize: "15px", color: "#374151" }}>
        <strong>{pending?.item?.name}</strong> has <strong>{pending?.count}</strong> upcoming booking(s). They keep their
        {" "}
        {labels.singular.toLowerCase()} and stay visible, but it will no longer be offered for new bookings.
      </p>
      <div className="d-flex gap-3 mt-4">
        <button onClick={onCancel} className="btn btn-outline-secondary px-4">
          Cancel
        </button>
        <SaveButton saving={busy} onClick={onConfirm} label="Deactivate" />
      </div>
    </SlidePanel>
  );
}

// The optional pool-picker list. An async wrapper so it ALWAYS yields a promise
// (never throws while the other requests are already in flight) and never fails.
async function loadCrewOptions() {
  try {
    return await resourceService.getResourceCrewOptions();
  } catch (e) {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 *  Tab
 * ------------------------------------------------------------------ */
// labels: { singular, plural, typeExample, nameExample } - chosen per industry
// in AdminSetupRevamp (inline ternaries, same as the other setup tabs).
function ResourcesTab({ labels }) {
  const [view, setView] = useState("resources");
  const [types, setTypes] = useState([]);
  const [resources, setResources] = useState([]);
  const [crewOptions, setCrewOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [resourcePanel, setResourcePanel] = useState({ show: false, editing: null });
  const [typePanel, setTypePanel] = useState({ show: false, editing: null });
  const [blocksFor, setBlocksFor] = useState(null);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [t, r, c] = await Promise.all([
        resourceService.getResourceTypes(),
        resourceService.getResources(),
        // Only a convenience for the pool picker: never let it break the screen.
        loadCrewOptions(),
      ]);
      setTypes(t?.success ? t.data : []);
      setResources(r?.success ? r.data : []);
      setCrewOptions(c?.success ? c.data : []);
    } catch (e) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeTypes = types.filter((t) => t.is_active);

  const closeResourcePanel = () => setResourcePanel({ show: false, editing: null });
  const closeTypePanel = () => setTypePanel({ show: false, editing: null });

  const toggleResource = async (item) => {
    if (item.is_active) {
      try {
        const usage = await resourceService.getResourceUsage(item.id);
        const count = usage?.data?.future_bookings || 0;
        if (count > 0) {
          setPendingDeactivate({ item, count });
          return;
        }
      } catch (e) {
        // Could not count: fall through and deactivate, the server is the authority.
      }
      await deactivateNow(item);
    } else {
      const res = await resourceService.updateResource(item.id, { is_active: 1 });
      if (res?.success) load();
      else toast.error(resourceService.resultMessage(res));
    }
  };

  const deactivateNow = async (item) => {
    const res = await resourceService.deactivateResource(item.id);
    if (res?.success) load();
    else toast.error(resourceService.resultMessage(res));
  };

  const confirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    setDeactivating(true);
    try {
      await deactivateNow(pendingDeactivate.item);
    } finally {
      setDeactivating(false);
      setPendingDeactivate(null);
    }
  };

  const toggleType = async (item) => {
    const res = item.is_active
      ? await resourceService.deactivateResourceType(item.id)
      : await resourceService.updateResourceType(item.id, { name: item.name, is_active: 1 });
    if (res?.success) load();
    else toast.error(resourceService.resultMessage(res));
  };

  const switchStyle = (key) => ({
    padding: "7px 18px",
    borderRadius: "20px",
    border: `1px solid ${view === key ? "#C2185B" : "#e3ebf8"}`,
    background: view === key ? "#C2185B" : "#fff",
    color: view === key ? "#fff" : "#6c757d",
    fontSize: "13px",
    fontWeight: view === key ? 600 : 400,
    cursor: "pointer",
  });

  const addAction =
    view === "resources"
      ? () => (activeTypes.length ? setResourcePanel({ show: true, editing: null }) : setView("types"))
      : () => setTypePanel({ show: true, editing: null });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#111827" }}>
            {labels.plural}
          </h5>
          <p className="mb-0" style={{ fontSize: "13px", color: "#6B7280" }}>
            Optional. Attach a {labels.singular.toLowerCase()} to a booking to reserve it for that time. There are no
            working hours to set up: it is busy only when a booking uses it or you block it.
          </p>
        </div>
        <button
          onClick={addAction}
          className="btn btn-danger"
          style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, height: "38px" }}
        >
          {view === "resources" ? (activeTypes.length ? `+ New ${labels.singular}` : "+ Add a type first") : "+ New Type"}
        </button>
      </div>

      <div className="d-flex gap-2 mb-3">
        <button type="button" style={switchStyle("resources")} onClick={() => setView("resources")}>
          {labels.plural}
        </button>
        <button type="button" style={switchStyle("types")} onClick={() => setView("types")}>
          Types
        </button>
      </div>

      {loadFailed && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
          <span>Could not load {labels.plural.toLowerCase()}.</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={load}>
            Retry
          </button>
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          {view === "resources" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Sl. No", "Name", "Type", "Crew", "Actions", "Active"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows columns={["30px", "160px", "100px", "110px", "60px", "50px"]} />
                ) : resources.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                      {activeTypes.length === 0
                        ? `Add a type (for example "${labels.typeExample}") first, then add your ${labels.plural.toLowerCase()}.`
                        : `No ${labels.plural.toLowerCase()} yet.`}
                    </td>
                  </tr>
                ) : (
                  resources.map((item, idx) => (
                    <ResourceRow
                      key={item.id}
                      item={item}
                      index={idx}
                      onEdit={(r) => setResourcePanel({ show: true, editing: r })}
                      onBlocks={setBlocksFor}
                      onToggle={toggleResource}
                    />
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Sl. No", "Type", `Active ${labels.plural}`, "Rename", "Active"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows columns={["30px", "160px", "60px", "40px", "50px"]} />
                ) : types.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                      No types yet. Add one, for example "{labels.typeExample}".
                    </td>
                  </tr>
                ) : (
                  types.map((item, idx) => (
                    <TypeRow
                      key={item.id}
                      item={item}
                      index={idx}
                      onEdit={(t) => setTypePanel({ show: true, editing: t })}
                      onToggle={toggleType}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ResourceFormPanel
        show={resourcePanel.show}
        editing={resourcePanel.editing}
        types={types}
        crewOptions={crewOptions}
        labels={labels}
        onHide={closeResourcePanel}
        onSaved={() => {
          closeResourcePanel();
          load();
        }}
      />
      <TypeFormPanel
        show={typePanel.show}
        editing={typePanel.editing}
        labels={labels}
        onHide={closeTypePanel}
        onSaved={() => {
          closeTypePanel();
          load();
        }}
      />
      <BlocksPanel show={!!blocksFor} resource={blocksFor} onHide={() => setBlocksFor(null)} />
      <DeactivatePanel
        pending={pendingDeactivate}
        labels={labels}
        busy={deactivating}
        onCancel={() => setPendingDeactivate(null)}
        onConfirm={confirmDeactivate}
      />
    </div>
  );
}

export default ResourcesTab;
